import React, { createRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';

import { PlayerSelectionModal } from './components/PlayerSelectionModal';
import { PlayerSelectionFilterItems } from './components/PlayerSelectionFilterItems';
import { PlayerSelectionSearchResults } from './components/PlayerSelectionSearchResults';
import { PlayerSearchInput } from '../../../../../../../../common/react/components/PlayerSearchInput';
import {
    PlayerSelectionPositionsFilters,
    PlayerSelectionTeamsFilters
} from './components/filters';
import { FilterButton } from '../../../../../../../../common/react/components/FilterButton';
import { getTranslation } from '../../../../../../../../common/react/utils/translations';

import {
    playerSelectionVisibleSelector,
    playerComparisonFiltersSelector
} from '../../../../../../store/js/modules/statspro/player-comparison/selectors';
import {
    CHANGE_PLAYER_SELECTION_MODAL_VISIBILITY,
    FETCH_SEARCH_RESULTS
} from '../../../../../../store/js/modules/statspro/player-comparison/actions';

const DEBOUNCE_TIMER = 500;

/**
 * @param root0
 * @param root0.compseason
 * @param root0.compseasonPid
 * @returns {JSX.Element} Component.
 */
export const PlayerSelection = ({ compseason, compseasonPid }) => {
    const dispatch = useDispatch();

    const containerRef = createRef();

    // Player Search
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    const [isLoading, setIsLoading] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const filters = useSelector(playerComparisonFiltersSelector);
    const posQuery = filters.positions.flat().join(',');
    const teamQuery = filters.teams.join(',');

    const selectSearchResults = createSelector(
        (state) => state.statspro.playerComparison?.searchResults.players,
        (players) => players
    );

    const searchResults = useSelector(selectSearchResults);

    const onSearchSubmit = async (newQuery) => {
        dispatch(
            FETCH_SEARCH_RESULTS.request({
                compseasonPid,
                newQuery,
                posQuery,
                teamQuery
            })
        );
        setIsLoading(true);
    };

    useEffect(() => {
        const timer = setTimeout(
            () => setQuery(debouncedQuery.trim()),
            DEBOUNCE_TIMER
        );
        return () => clearTimeout(timer);
    }, [debouncedQuery]);

    useEffect(() => {
        if (query !== '' && query.length > 0) {
            onSearchSubmit(query);
        } else {
            onSearchSubmit('');
        }
    }, [query, posQuery, teamQuery]);

    useEffect(() => {
        if (isLoading) {
            setIsLoading(false);
        }
    }, [searchResults]);

    // Modal control
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);

    const { modalVisible } = useSelector((state) => ({
        // @ts-ignore
        modalVisible: playerSelectionVisibleSelector(state)
    }));

    const changeModalVisibility = (isVisible) => {
        return dispatch(
            CHANGE_PLAYER_SELECTION_MODAL_VISIBILITY.request(isVisible)
        );
    };

    const title = getTranslation('label.statspro.playerComparison.addPlayers');

    useEffect(() => {
        modalVisible === true ? setIsActive(true) : setIsActive(false);
    }, [modalVisible]);

    return modalVisible ? (
        <div className="player-selection">
            <PlayerSelectionModal
                icon="compare"
                title={title}
                isVisible={modalVisible}
                hideModal={() => changeModalVisibility(false)}
            >
                <div className="player-selection__items-container">
                    <div
                        ref={containerRef}
                        className="player-selection__nav"
                        tabIndex="0"
                    >
                        <PlayerSearchInput
                            setDebouncedQuery={setDebouncedQuery}
                            isActive={isActive}
                            query={query}
                        />

                        <div className="player-selection-nav__filter-button-wrapper">
                            <FilterButton
                                onChange={setAreFilterOptionsVisible}
                            />
                        </div>
                    </div>

                    {areFilterOptionsVisible && (
                        <PlayerSelectionFilterItems>
                            <PlayerSelectionPositionsFilters />
                            <PlayerSelectionTeamsFilters
                                compseason={compseason}
                            />
                        </PlayerSelectionFilterItems>
                    )}

                    {searchResults && (
                        <PlayerSelectionSearchResults
                            isLoading={isLoading}
                            query={query}
                            results={searchResults}
                            seasonId={compseasonPid}
                        />
                    )}
                </div>
            </PlayerSelectionModal>
        </div>
    ) : (
        ''
    );
};

PlayerSelection.propTypes = {
    compseason: PropTypes.string.isRequired,
    compseasonPid: PropTypes.string.isRequired
};
